import {defineStore} from "pinia";
import localforage from "localforage";
import axios from "axios";
import {v4 as uuidv4} from "uuid";
import {replaceObjectInArray,
    areDatesEqual,
    mergeArrays,
    removeObjectFromArray,
    currentTimestamp,
    mysqlDate,
    deepCopyObject} from "@/utils/utils";

const API_ROUTE = "/api/v1";
const AXIOS_TIMEOUT = 15000;

export const apiStore = defineStore("api", {
    state: () => ({
        isOnline : true,
        errorCount : 0,
        clientSearchResult : null,
        measurementType: "measurement"
    }),

    getters: {
        loggedInUser() {
            return this.app_data.loggedin_user;
        },

        loggedInSales() {
            return this.app_data.loggedin_sales;
        },

        isSalesLoggedIn() {
            return this.loggedInSales !== null && this.loggedInSales !== undefined;
        },

        shop() {
            return this.app_data.shop;
        }
    },

    actions: {
        async init(baseUrl, client_id, client_secret) {
            try {
                this.baseUrl = baseUrl;
                this.clientId = client_id;
                this.clientSecret = client_secret;
                this.app_data = {
                    sales : [],
                    appointments : []
                };

                await this.loadAccessToken();
                if (this.accessToken === null) {
                    this.logoutUser();
                    return;
                }
                await this.loadLocalData();
            } catch (error) {
                console.log("api init error ", error);
            }
        },

        async loadLocalData() {
            let cachedData = await localforage.getItem("app_data_2");
            if (cachedData !== null) {
                Object.assign(this.app_data, cachedData);
            }
        },

        async loadAccessToken() {
            this.accessToken = await localforage.getItem("access_token");

            if (typeof this.accessToken === "string") {
                this.accessToken = null;
            }
        },

        setAccessTokenFromResponse(response) {
            if (response.access_token !== undefined) {
                response["expires_at"] = Math.round(new Date().getTime() / 1000 + response.expires_in);
                this.accessToken = response;
                localforage.setItem("access_token", JSON.parse(JSON.stringify(this.accessToken)));
            }
        },

        async validateToken() {
            try {
                return await this.doApiCall("get", API_ROUTE + "/validate-token");
            }
            catch (error) {
                throw ("validateToken", error);
            }
        },

        async pingServer() {
            try {
                if (this.pingSource !== undefined) {
                    this.pingSource.cancel();
                }
                this.pingSource = axios.CancelToken.source();
                await axios.get(this.baseUrl, {
                    timeout : 7500,
                    cancelToken: this.pingSource.token
                });
                this.changeOnlineMode(true);
            } catch (error) {
                console.log(error.message);
                this.changeOnlineMode(false);
            } finally {
                this.pingSource = undefined;
            }
        },

        async updatePassword(oldPassword, newPassword) {
            return await this.doApiCall("post", API_ROUTE + "/user/update-password", {oldPassword, newPassword});
        },

        /*
            login / logout
        */
        async loginUser(email, password) {
            try {
                let data = {
                    grant_type : "password",
                    client_id : this.clientId,
                    client_secret : this.clientSecret,
                    username : email,
                    password : password
                };

                const response = await this.doApiCall("post", "/oauth/token", data);
                this.setAccessTokenFromResponse(response);

                return await this.setCurrentUser();
            } catch (error) {
                return null;
            }
        },

        async setCurrentUser() {
            const user = await this.getCurrentUser();

            if (user.message === "Unauthenticated.") {
                this.logout();

            } else {
                if (user.active === true) {
                    this.currentUser = user;
                    this.app_data.loggedin_user = user;
                    this.app_data.shop = user.shop;
                    this.saveAppData();
                    //this.trigger(this.LOGIN_USER);
                    return user;

                } else {
                    this.logout();
                }
            }
        },

        setUserShop(shop) {
            this.app_data.shop = shop;
            this.app_data.shop_id = shop.id;
            this.app_data.loggedin_user.shop_id = shop.id;
            this.app_data.loggedin_user.shop = shop;
            this.saveAppData();
        },

        async getCurrentUser() {
            return this.doApiCall("get", API_ROUTE + "/user");
        },

        async forgotPassword(email) {
            try {
                const response = await this.doApiCall("post", API_ROUTE + "/user/reset-password", {email});
                return response.message;
            } catch (error) {
                console.log("forgotPassword", error);
            }
        },

        setLoggedInSales(sales) {
            this.app_data.loggedin_sales = sales;
            this.saveAppData();
        },

        logoutUser() {
            this.app_data.loggedin_user = null;
            this.app_data.loggedin_sales = null;
            this.app_data.appointments = [];
            this.app_data.shop = null;
            delete this.app_data.shop_id;
            delete this.app_data.last_appointment_check;
            this.saveAppData();
            localforage.removeItem("access_token");
        },

        logoutCode() {
            this.app_data.loggedin_code = null;
            this.app_data.loggedin_sales = null;
            this.saveAppData();
        },

        /**
         * sales
         */
        async getSalesForShop() {
            if (this.isOnline) {
                try {
                    const response = await this.doApiCall("get", API_ROUTE + "/shop/" + this.loggedInUser.shop_id + "/sales");
                    this.app_data.sales = response.filter(x => x.deleted_at === null);
                    this.saveAppData();

                } catch (error) {
                    console.log("getSales error ", error);
                }
            }
            return this.app_data.sales
                .filter(x => x.shop_id === this.loggedInUser.shop_id)
                .filter(x => x.deleted_at === null).sort((x, y) => x.firstname < y.firstname ? -1 : 1);
        },

        async createSales(data) {
            if (this.isOnline) {
                try {
                    data.id = uuidv4();
                    data = await this.doApiCall("put", API_ROUTE + "/sales", data);

                } catch (error) {
                    console.log("create sales error ", error);

                }
            }
        },

        async updateSales(data) {
            if (this.isOnline) {
                try {
                    data = await this.doApiCall("post", API_ROUTE + "/sales", data);
                    this.app_data.sales = replaceObjectInArray(this.app_data.sales, data);
                    this.saveAppData();

                } catch (error) {
                    console.log("update sales error");
                }
            }

            return data;
        },

        async deleteSales(data) {
            data.deleted_local = true;

            if (this.isOnline) {
                try {
                    await this.doApiCall("delete", API_ROUTE + "/sales/" + data.id);
                    this.app_data.sales = removeObjectFromArray(this.app_data.sales, data);

                } catch (error) {
                    console.log("delete sales error ", error);
                }
            }

            this.app_data.sales = replaceObjectInArray(this.app_data.sales, data);
            this.saveAppData();
        },

        /****
            appointments
        */
        // date === 'none' : all unplanned appointments
        // date === null : all appointments
        async getAppointments(date = null) {
            this.updateOfflineAppointmentUpdates();

            if (this.isOnline) {
                try {
                    const dateParam = date !== null ? "?date=" + date : "";
                    const appointments = await this.doApiCall("get", API_ROUTE + "/shop/" + this.loggedInUser.shop_id + "/appointments" + dateParam);
                    if (date === null) {
                        this.app_data.appointments = appointments;
                    } else {
                        this.app_data.appointments = mergeArrays(this.app_data.appointments, appointments);
                    }
                    this.saveAppData();

                } catch (error) {
                    console.log("getAppointments", error);
                }
            }

            let appointments = this.filterAppointments();
            if (date !== null) {
                if (date === "none") {
                    return appointments.filter(x => x.date === null);
                } else {
                    return appointments.filter(x => areDatesEqual(x.date, date));
                }
            }
            return appointments;
        },

        //fetch updated appointments and update locally
        async getUpdatedAppointments() {
            try {
                const cacheKey = "last_appointment_check";
                let timestamp = await localforage.getItem(cacheKey);
                localforage.setItem(cacheKey, currentTimestamp());

                const appointments = await this.doApiCall("get", API_ROUTE + "/shop/" + this.loggedInUser.shop_id + "/updated-appointments?date=" + timestamp);
                if (appointments.length > 0) {
                    this.app_data.appointments = mergeArrays(this.app_data.appointments, appointments);
                    this.saveAppData();
                }

            } catch (error) {
                console.log("getUpdatedAppointments error", error);
            }
        },

        searchAppointments(search) {
            search = search.toLowerCase();
            return this.filterAppointments()
                .filter(x => x.clients.filter(x =>
                    (x.firstname && x.firstname.toLowerCase().indexOf(search) === 0) ||
                        (x.lastname && x.lastname.toLowerCase().indexOf(search) === 0) ||
                        (x.firstname + " " + x.lastname).toLowerCase().indexOf(search) === 0
                ).length > 0);
        },

        filterAppointments() {
            let appointments = this.app_data.appointments
                .filter(x => x.shop_id === this.loggedInUser.shop_id)
                .filter(x => x.deleted_at === undefined || x.deleted_at === null || x.deleted_local === true);

            return appointments;
        },

        async getAppointment(id) {
            const appointment = this.app_data.appointments.find(x => x.id === id);

            if (this.isOnline) {
                try {
                    return await this.doApiCall("get", API_ROUTE + "/appointments/" + id);

                } catch (error) {
                    console.log("getAppointment error", error);
                    return appointment;
                }
            } else {
                return appointment;
            }
        },

        async createAppointmentWithClient(client, language, isStaff = false) {
            client["sales_id"] = this.loggedInSales.id;
            client["shop_id"] = this.loggedInUser.shop_id;
            client["id"] = uuidv4();
            client["language"] = language;
            client["type"] = this.measurementType;
            if (isStaff) {
                client["type"] = "staff";
            }

            let newAppointment = null;
            if (this.isOnline) {
                try {
                    newAppointment = await this.doApiCall("put", API_ROUTE + "/appointments", client);

                } catch (error) {
                    console.log("createAppointmentWithClient error", error);
                }
            }

            //create local version when offline
            if (newAppointment === null) {
                newAppointment = {
                    id : uuidv4(),
                    updated_local : true,
                    date : null,
                    sales_id : this.loggedInSales.id,
                    shop_id: this.loggedInUser.shop_id,
                    clients : [
                        client
                    ]
                };
            }
            this.app_data.appointments.push(newAppointment);
            this.saveAppData();

            return newAppointment;
        },

        async addClientToAppointment(client, appointment) {
            client["id"] = uuidv4();
            client["shop_id"] = this.loggedInUser.shop_id;
            client["sales_id"] = this.loggedInSales.id;

            let updatedAppointment = null;
            if (this.isOnline) {
                try {
                    updatedAppointment = await this.doApiCall("post", API_ROUTE + "/appointments/" + appointment.id + "/add-client", client);

                } catch (error) {
                    console.log("addClientToAppointment", error);
                }
            }

            if (updatedAppointment === null) {
                updatedAppointment = appointment;
                updatedAppointment.updated_local = true;
            }
            this.app_data.appointments = replaceObjectInArray(this.app_data.appointments, updatedAppointment);
            this.saveAppData();

            return updatedAppointment;
        },

        async deleteAppointment(appointment) {
            appointment.deleted_at = new Date();
            appointment.deleted_local = true;
            this.app_data.appointments = replaceObjectInArray(this.app_data.appointments, appointment);

            if (this.isOnline) {
                try {
                    await this.doApiCall("delete", API_ROUTE + "/appointments/" + appointment.id);
                    this.app_data.appointments = removeObjectFromArray(this.app_data.appointments, appointment);

                } catch (error) {
                    console.log("deleteAppointment error", error);
                }
            }

            this.saveAppData();
        },

        async saveMeasurementResult(appointmentId, resultData) {
            let appointment = await this.getAppointment(appointmentId);
            if (!appointment) return;

            for (let i = 0; i < appointment.clients.length; i++) {
                const side = appointment.clients[i].additional_info.bedside;
                let result = resultData.find(x => x.SideOfBed.trim() === side.trim());
                appointment.clients[i].ergosleep_id = result.ergosleep_id;
                appointment.clients[i].measurement_result = result;
                appointment.clients[i].measurement_date = mysqlDate(new Date());
            }

            this.updateAppointment(appointment);
        },

        async saveMovieCompleted(appointmentId) {
            let appointment = await this.getAppointment(appointmentId);
            if (!appointment) return;

            appointment.measurement_done = true;
            this.updateAppointment(appointment);
        },

        async updateAppointment(appointment) {
            appointment = deepCopyObject(appointment); //prevent reactivity back in app
            appointment.clients = appointment.clients.filter(x => x.id !== undefined);
            if (!appointment.type) {
                appointment.type = this.measurementType;
            }

            if (this.isOnline) {
                try {
                    appointment = await this.doApiCall("post", API_ROUTE + "/appointments/" + appointment.id, appointment);
                } catch (error) {
                    console.log("updateAppointment error", error);
                }
            }

            this.app_data.appointments = replaceObjectInArray(this.app_data.appointments, appointment);
            this.saveAppData();

            return appointment;
        },

        async forceAppointmentCompleted(ergosleepId) {
            await this.doApiCall("post", API_ROUTE + "/appointments/force-completed/" + ergosleepId);
        },

        async updateOfflineAppointmentUpdates() {
            if (!this.isOnline || this.app_data.appointments === undefined) return;

            const updatedAppointments = this.app_data.appointments.filter(s => s.updated_local);
            updatedAppointments.forEach(async appointment => {
                await this.createOrUpdateAppointment(appointment);
                appointment.updated_local = false;
            });

            const deletedAppointments = this.app_data.appointments.filter(s => s.deleted_local === true);
            deletedAppointments.forEach(async appointment => {
                await this.deleteAppointment(appointment);
            });
        },

        async createOrUpdateAppointment(appointment) {
            try {
                await this.doApiCall("post", API_ROUTE + "/appointments/" + appointment.id + "/update-from-cache", appointment);

            } catch (error) {
                console.log("createOrUpdateAppointment ", error);
            }
        },

        async updateClient(client) {
            let appointment = this.findAppointmentForClient(client);
            appointment.updated_local = true;
            appointment.clients = replaceObjectInArray(appointment.clients, client);

            if (this.isOnline) {
                try {
                    const updatedAppointment = await this.doApiCall("post", API_ROUTE + "/client/" + client.id, client);
                    //update appointment with new client data
                    this.app_data.appointments = replaceObjectInArray(this.app_data.appointments, updatedAppointment);

                } catch (error) {
                    console.log(error);
                }
            }

            this.saveAppData();
        },

        findAppointmentForClient(client) {
            return this.app_data.appointments.find(x => x.clients && x.clients.some(x => x.id === client.id));
        },

        async getTimeslots(date, shopHours, slotDuration, cocons) {
            const appointments = await this.getAppointments(date);
            const usedSlots = appointments.map(x => x.date.substr(11, 5));

            let startTime = this.getHourFromSlot(shopHours[0].from);
            startTime = Math.floor(startTime / slotDuration) * slotDuration;
            let endTime = this.getHourFromSlot(shopHours[shopHours.length - 1].to);
            endTime = Math.floor(endTime / slotDuration) * slotDuration - slotDuration;

            let slots = [];
            for (let time = startTime; time <= endTime; time += slotDuration) {
                let hour = Math.floor(time / 60);
                let minutes = time - hour * 60 ;

                if (hour < 10) {
                    hour = "0" + hour;
                }
                if (minutes < 10) {
                    minutes = "0" + minutes;
                }
                const timeString = hour + ":" + minutes;

                if (this.isSlotInShopHours(60 * parseInt(hour) + parseInt(minutes), shopHours, slotDuration)) {
                    slots.push({
                        time : timeString,
                        free_slots : cocons - usedSlots.filter(x => x === timeString).length
                    });
                }

            }
            return slots;
        },

        getHourFromSlot(slot) {
            const arr = slot.split(":");
            if (arr.length === 2) {
                return parseInt(arr[0]) * 60 + parseInt(arr[1]);
            }
            console.log("slot parse error");
            return 0;
        },

        //time in minutes
        isSlotInShopHours(time, shopHours, slotDuration) {
            for (const hours of shopHours) {
                if (this.getHourFromSlot(hours.from) <= time && time <= this.getHourFromSlot(hours.to) - slotDuration) {
                    return true;
                }
            }
            return false;
        },

        async getErgosleepUserById(code) {
            if (this.isOnline) {
                try {
                    return this.doApiCall("post", API_ROUTE + "/client/search/code", {code});
                } catch (error) {
                    console.log("get ergosleep user id", error);
                }
            }
            return {client : this.getOfflineErgosleepUser(code)};
        },

        getOfflineErgosleepUser(code) {
            const appointment = this.app_data.appointments.find(x => {
                return x.clients.find(x => x.ergosleep_id === code);
            });
            if (appointment) {
                return appointment.clients.find(x => x.ergosleep_id === code);
            }
            return null;
        },

        searchErgosleepUser(data) {
            return this.doApiCall("post", API_ROUTE + "/client/search/form", data);
        },

        mailMeasurementResultToClient(ergosleep_id, email, locale) {
            if (locale.length > 2) {
                locale = locale.substring(2);
            }
            return this.doApiCall("post", API_ROUTE + "/client/email-result/" + email, {ergosleep_id, locale});
        },

        async searchPostalCodes(country, search) {
            return await this.doApiCall("post", API_ROUTE + "/postal-codes", {country, search});
        },

        async searchStreet(country, postalCode, search) {
            return await this.doApiCall("post", API_ROUTE + "/search-street", {country, postalCode, search});
        },

        async getAppointmentForErgosleepId(ergosleep_id) {
            const clients = await this.doApiCall("get", API_ROUTE + "/appointments/ergosleepid/" + ergosleep_id);
            return clients.slice(0, 2);
        },

        async rescheduleClients(ergosleep_ids, language) {
            let ids = [];

            if (typeof ergosleep_ids === "string") {
                ids = [ergosleep_ids];
            } else if (typeof ergosleep_ids === "object") {
                ids = ergosleep_ids;
            }
            const data = {
                ergosleep_ids : ids,
                sales_id : this.loggedInSales.id,
                language,
                shop_id: this.loggedInUser.shop_id
            };
            const result = await this.doApiCall("post", API_ROUTE + "/appointments/reschedule", data);
            const appointment = result.appointment;
            this.app_data.appointments.push(appointment);
            this.saveAppData();

            return appointment;
        },

        /*
            service appointment
        */
        async getServiceAppointment() {
            try {
                const reponse = await this.doApiCall("get", API_ROUTE + "/service-appointments/" + this.loggedInUser.shop_id);
                return reponse.appointment;
            } catch (error) {

                console.log("getServiceAppointment", error);
                const serviceAppointment = this.app_data.appointments.find(x => {
                    return x.clients.length > 0 && x.clients[0].lastname === "Service";
                });
                if (serviceAppointment) {
                    return serviceAppointment;
                }
                const client = {
                    "lastname": "Service",
                    "firstname": "Service",
                    "email": null,
                    "additional_info": {
                        "bedside": "left",
                        "gender": "m",
                        "size": 190,
                        "sleep_position": "side",
                        "base": "base",
                        "optin": true,
                        "gdpr": true
                    }
                };
                const appointment = await this.createAppointmentWithClient(client, "be-nl");
                const date = new Date();
                const month = date.getMonth() + 1;
                const day = date.getDate();
                const dateSTring = date.getFullYear() + "-" + (month < 10 ? "0" : "") + month + "-" + (day < 10 ? "0" : "") + day;
                appointment.date = dateSTring + " 10:00:00";
                this.updateAppointment(appointment);
                console.log(appointment);
                return appointment;
            }
        },

        async getServiceUsers() {
            return await this.doApiCall("get", API_ROUTE + "/service-users");
        },

        async createServiceUser(data) {
            return await this.doApiCall("post", API_ROUTE + "/create-service-user", data);
        },

        async checkIfServiceUser(email) {
            return await this.doApiCall("get", API_ROUTE + "/service-user-check?email=" + email);
        },

        /*
            shops admin
        */
        async getShops() {
            return await this.doApiCall("get", API_ROUTE + "/shops");
        },

        async getShop(id) {
            if (this.isOnline) {
                try {
                    const shop = await this.doApiCall("get", API_ROUTE + "/shops/" + id);
                    this.app_data.shop = shop;
                    this.saveAppData();
                    return shop;
                } catch (error) {
                    console.log("getShop", error);
                    return this.app_data.shop;
                }
            } else {
                return this.app_data.shop;
            }
        },

        async createShop(data) {
            return await this.doApiCall("put", API_ROUTE + "/shops", data);
        },

        async updateShop(data) {
            const shop = await this.doApiCall("post", API_ROUTE + "/shops/" + data.id, data);
            this.app_data.shop = shop;
            this.saveAppData();
        },

        async getUsersForShop(id) {
            return await this.doApiCall("get", API_ROUTE + "/shops/" + id + "/users");
        },

        async createUserForShop(shopId, data) {
            return await this.doApiCall("put", API_ROUTE + "/shops/" + shopId + "/create-user", data);
        },

        async updateUser(user) {
            return await this.doApiCall("post", API_ROUTE + "/user/" + user.id, user);
        },

        async deleteUser(userId) {
            return await this.doApiCall("delete", API_ROUTE + "/user/" + userId);
        },

        /*
            ip
        */
        async getIp() {
            return await this.doApiCall("get", API_ROUTE + "/ip");
        },

        /**
         * api call base method
         */
        async doApiCall(method, endPoint, data = {}) {
            try {
                const url = (endPoint.indexOf("http") === -1 ? this.baseUrl : "") + endPoint;
                let headers = {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                };
                if (this.accessToken !== null) {
                    const expired = new Date().getTime() / 1000 > this.accessToken.expires_at;
                    if (expired) {
                        this.logoutUser();
                    }
                    headers.Authorization = "Bearer " + this.accessToken.access_token;
                }
                const response = await axios({
                    method: method,
                    url: url,
                    headers: headers,
                    data : data,
                    timeout : AXIOS_TIMEOUT
                });

                this.changeOnlineMode(true);

                return response.data;

            } catch (error) {
                console.log("api error ", error);

                if (error.response === undefined) {
                    //no response, server unreachable
                    console.log("server not found - offline mode ");
                    this.changeOnlineMode(false);
                }

                // logout if unauthorized error code is returned
                if (error.response && error.response.status === 401) {
                    this.logoutUser();
                }

                throw (error);
            }
        },

        changeOnlineMode(online) {
            if (online) {
                this.errorCount = 0;
            } else {
                this.errorCount++;
            }

            const shouldChangeStatus = online || (this.errorCount >= 3 && this.isOnline);
            if (shouldChangeStatus) {
                this.isOnline = online;
            }
        },

        saveAppData() {
            // turn a reactive object into a plain object
            const app_data = JSON.parse(JSON.stringify(this.app_data));
            localforage.setItem("app_data_2", app_data);
        }
    }
});